import React, {useEffect} from 'react';
import {create} from 'zustand';

import LiveRegion from 'lib/LiveRegion';

interface AnnouncementState {
  message: string;
}

const useAnnouncementStore = create<AnnouncementState>()(() => ({message: ''}));

const ANNOUNCEMENT_CLEAR_MS = 7000;

/** Speaks `message` to screen-reader students through the mounted AnnotationAnnouncer. */
export const announceAnnotationChange = (message: string): void => {
  useAnnouncementStore.setState(({message: last}) => ({
    // A repeat must still change the text, or screen readers stay silent.
    message: last === message ? `${message}​` : message
  }));
};

/**
 * The polite live region annotation changes are spoken through. Mounted once per
 * practice surface, outside the editors, so it outlives the one that announces.
 * Each message clears after a few seconds, so browsing the question later skips it.
 */
export const AnnotationAnnouncer = () => {
  const message = useAnnouncementStore((state) => state.message);
  useEffect(() => {
    useAnnouncementStore.setState({message: ''});
  }, []);
  useEffect(() => {
    if (!message) {
      return undefined;
    }
    const timeout = setTimeout(
      () => useAnnouncementStore.setState({message: ''}),
      ANNOUNCEMENT_CLEAR_MS
    );
    return () => clearTimeout(timeout);
  }, [message]);

  return (
    <LiveRegion assertive={false} className='sr-only'>
      {message}
    </LiveRegion>
  );
};
